import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ClassStudentSelect } from "@/components/ClassStudentSelect";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { DAYS, GRADE_GROUPS, MONTHS } from "@/lib/subjects";
import { LayoutGrid, Plus, Trash2, Save, Users, UserRound, X, Pencil } from "lucide-react";
const SHORT_DAYS = ["PZT", "SAL", "ÇAR", "PER", "CUM", "CMT", "PAZ"];
const emptyDay = () => ({ unit: "", pages: "", target: "", dogru: 0, yanlis: 0, bos: 0, filled: false, status: "bekliyor" });

const expandWeeks = (weeks) => (weeks || []).map((w) => ({
  ...w,
  rows: (w.rows || []).map((r) => {
    const cells = { ...(r.cells || {}) };
    DAYS.forEach((d) => { if (!cells[d]) cells[d] = emptyDay(); });
    return { ...r, cells };
  }),
}));

const shrinkWeeks = (weeks) => weeks.map((w) => ({
  week_number: w.week_number, title: w.title || "", month: w.month || "",
  rows: w.rows.map((r) => {
    const cells = {};
    DAYS.forEach((d) => {
      const c = r.cells?.[d];
      if (!c) return;
      const d0 = Number(c.dogru) || 0, y0 = Number(c.yanlis) || 0, b0 = Number(c.bos) || 0;
      const hasTask = (c.unit || "").trim() || (c.pages || "").trim() || Number(c.target) > 0;
      const hasResult = d0 + y0 + b0 > 0;
      if (!hasTask && !hasResult) return;
      const filled = hasResult || !!c.filled;
      const status = c.status === "onayli" ? "onayli" : filled ? "tamamlandi" : "bekliyor";
      cells[d] = { unit: c.unit || "", pages: c.pages || "", target: Number(c.target) || 0, dogru: d0, yanlis: y0, bos: b0, filled, status };
    });
    return { subject_name: r.subject_name, sublabel: r.sublabel || "", color: r.color || "sky", cells };
  }),
}));

export default function AdminStudyProgram() {
  const [params] = useSearchParams();
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [targetType, setTargetType] = useState(params.get("grade") ? "class" : "student"); // 'student' | 'class'
  const [studentId, setStudentId] = useState(params.get("student") || "");
  const [clsGrade, setClsGrade] = useState(Number(params.get("grade")) || 5);
  const [clsSection, setClsSection] = useState(params.get("section") || "");
  const [title, setTitle] = useState("");
  const [weeks, setWeeks] = useState([{ week_number: 1, title: "", month: "", rows: [] }]);
  const [activeWi, setActiveWi] = useState(0);
  const [saving, setSaving] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [assignFor, setAssignFor] = useState(null); // hafta index
  const [assignType, setAssignType] = useState("student");
  const [assignStudent, setAssignStudent] = useState("");
  const [assignGrade, setAssignGrade] = useState(5);
  const [assignSection, setAssignSection] = useState("");
  const [titleEditWi, setTitleEditWi] = useState(null);
  const [programId, setProgramId] = useState(params.get("program") || null);
  const [programList, setProgramList] = useState([]);
  const [clsTitles, setClsTitles] = useState([]);
  const [clsSelTitle, setClsSelTitle] = useState(null);
  const programParam = params.get("program");

  useEffect(() => {
    api.get("/students").then(({ data }) => setStudents(data));
    api.get("/subjects").then(({ data }) => setSubjects(data));
  }, []);

  const applyProgram = (data) => {
    if (data?.weeks?.length) {
      setWeeks(expandWeeks(data.weeks));
      setTitle(data.title || "");
      setProgramId(data.id);
    } else {
      setWeeks([{ week_number: 1, title: "", month: "", rows: [] }]);
      setTitle("");
      setProgramId(null);
    }
    setActiveWi(0);
  };

  const loadProgram = (pid) => {
    if (!studentId) return;
    api.get("/study-program", { params: { student_id: studentId, ...(pid ? { program_id: pid } : {}) } }).then(({ data }) => applyProgram(data));
  };

  const refreshProgramList = () => {
    if (targetType === "student" && studentId) api.get("/study-programs/list", { params: { student_id: studentId } }).then(({ data }) => setProgramList(data));
  };

  useEffect(() => {
    if (targetType === "student" && studentId) {
      refreshProgramList();
      loadProgram(programParam || undefined);
    }
  }, [studentId, targetType, programParam]);

  const newProgram = () => {
    setProgramId(null);
    setTitle("");
    setWeeks([{ week_number: 1, title: "", month: "", rows: [] }]);
    setActiveWi(0);
    toast.info("Yeni program: kaynak/yayınevi adını girin, hafta ekleyin ve kaydedin — ayrı bir program olarak oluşur.");
  };

  // Sınıf modu: yayınevi bazlı programlar
  const loadClassTemplate = (t) => {
    api.get("/study-program/class-template", { params: { grade: clsGrade, section: clsSection || undefined, title: t } }).then(({ data }) => {
      if (data?.weeks?.length) {
        setWeeks(expandWeeks(data.weeks));
        setTitle(data.title || "");
        setClsSelTitle(data.title || "");
      } else {
        setWeeks([{ week_number: 1, title: "", month: "", rows: [] }]);
        setTitle("");
        setClsSelTitle(null);
      }
      setActiveWi(0);
    }).catch(() => {});
  };
  const refreshClassTitles = (loadFirst) => api.get("/study-program/class-titles", { params: { grade: clsGrade, section: clsSection || undefined } }).then(({ data }) => {
    setClsTitles(data);
    if (loadFirst) {
      if (data.length) loadClassTemplate(data[0].title);
      else { setWeeks([{ week_number: 1, title: "", month: "", rows: [] }]); setTitle(""); setClsSelTitle(null); setActiveWi(0); }
    }
  }).catch(() => {});
  useEffect(() => {
    if (targetType === "class" && clsGrade) refreshClassTitles(true);
  }, [targetType, clsGrade, clsSection]);
  const newClassProgram = () => {
    setClsSelTitle(null);
    setTitle("");
    setWeeks([{ week_number: 1, title: "", month: "", rows: [] }]);
    setActiveWi(0);
    toast.info("Yeni program: yeni yayınevi adını girin, haftaları ekleyin ve 'Tüm Sınıfa Kaydet' deyin — mevcut programlar silinmez, ayrı program olarak eklenir.");
  };

  const deleteProgram = async () => {
    if (targetType === "student") {
      if (!studentId) return toast.error("Önce öğrenci seçin");
      if (!window.confirm("Bu öğrencinin çalışma programı silinsin mi?")) return;
      try {
        await api.delete("/study-program", { params: { student_id: studentId, ...(programId ? { program_id: programId } : {}) } });
        toast.success("Program silindi");
        refreshProgramList();
        setProgramId(null);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return; }
    } else {
      if (clsSelTitle === null) return toast.error("Silmek için önce yukarıdan bir yayınevi programı seçin");
      if (!window.confirm(`${clsGrade}. Sınıf${clsSection ? " / " + clsSection : " (tüm şubeler)"} öğrencilerinin "${clsSelTitle || "Başlıksız"}" programı silinsin mi? (Diğer yayınevi programları kalır)`)) return;
      try {
        const { data } = await api.delete("/study-program/class", { params: { grade: clsGrade, section: clsSection || undefined, title: clsSelTitle } });
        toast.success(`${data.count} öğrencinin programı silindi`);
        refreshClassTitles(false);
        setClsSelTitle(null);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return; }
    }
    setWeeks([{ week_number: 1, title: "", month: "", rows: [] }]);
    setTitle("");
    setActiveWi(0);
  };

  const addWeek = () => {
    const n = weeks.length ? Math.max(...weeks.map((w) => w.week_number)) + 1 : 1;
    const lastMonth = weeks.length ? weeks[weeks.length - 1].month || "" : "";
    setWeeks([...weeks, { week_number: n, title: "", month: lastMonth, rows: [] }]);
    setActiveWi(weeks.length);
  };

  const removeWeek = (wi) => {
    if (!window.confirm(`${weeks[wi].week_number}. hafta silinsin mi?`)) return;
    setWeeks(weeks.filter((_, i) => i !== wi));
    setActiveWi((a) => Math.max(0, wi <= a ? a - 1 : a));
  };

  const setWeekMonth = (wi, month) => setWeeks(weeks.map((w, i) => (i === wi ? { ...w, month } : w)));
  const setWeekTitle = (wi, title) => setWeeks(weeks.map((w, i) => (i === wi ? { ...w, title } : w)));

  const addRow = (wi) => {
    const sub = subjects[0];
    if (!sub) return toast.error("Önce ders tanımlayın");
    setWeeks(weeks.map((w, i) => (i === wi
      ? { ...w, rows: [...w.rows, { subject_name: sub.name, sublabel: "", color: sub.color, cells: Object.fromEntries(DAYS.map((d) => [d, emptyDay()])) }] }
      : w)));
  };

  const removeRow = (wi, ri) => setWeeks(weeks.map((w, i) => (i === wi ? { ...w, rows: w.rows.filter((_, j) => j !== ri) } : w)));

  const updateRow = (wi, ri, patch) => setWeeks(weeks.map((w, i) => {
    if (i !== wi) return w;
    return { ...w, rows: w.rows.map((r, j) => (j === ri ? { ...r, ...patch } : r)) };
  }));

  const pickSubject = (wi, ri, name) => {
    const sub = subjects.find((s) => s.name === name);
    if (sub) updateRow(wi, ri, { subject_name: sub.name, color: sub.color });
  };

  const updateCellField = (wi, ri, day, field, value) => {
    setWeeks(weeks.map((w, i) => {
      if (i !== wi) return w;
      const rows = w.rows.map((r, j) => {
        if (j !== ri) return r;
        const cells = { ...(r.cells || {}) };
        cells[day] = { ...(cells[day] || emptyDay()), [field]: value };
        return { ...r, cells };
      });
      return { ...w, rows };
    }));
  };

  const saveSingle = async () => {
    if (!studentId) return toast.error("Lütfen bir öğrenci seçin");
    const wasExisting = !!programId;
    setSaving(true);
    try {
      const { data } = await api.post("/study-program", { student_id: studentId, title, weeks: shrinkWeeks(weeks), program_id: programId || undefined });
      setProgramId(data.id);
      refreshProgramList();
      toast.success(wasExisting ? "Program güncellendi ✓" : "Yeni program kaydedildi ✓");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveClass = async () => {
    if (!weeks.length) return toast.error("Önce program oluşturun");
    const t = (title || "").trim();
    if (!t) return toast.error("Kaynak / Program (yayınevi) adını girin");
    const exists = clsTitles.some((c) => c.title === t);
    const cls = `${clsGrade}. Sınıf${clsSection ? " / " + clsSection : " (tüm şubeler)"}`;
    if (!window.confirm(exists ? `${cls} öğrencilerinin "${t}" programı güncellenecek. Onaylıyor musunuz?` : `${cls} öğrencilerine "${t}" YENİ program olarak eklenecek (mevcut programlar korunur). Onaylıyor musunuz?`)) return;
    setSaving(true);
    try {
      const { data } = await api.post("/study-program/assign-class", { grade: clsGrade, section: clsSection || null, title: t, weeks: shrinkWeeks(weeks) });
      toast.success(`${data.count} öğrenciye "${t}" programı ${exists ? "güncellendi" : "eklendi"} ✓`);
      setClsSelTitle(t);
      refreshClassTitles(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doAssignWeek = async () => {
    const wk = shrinkWeeks([weeks[assignFor]])[0];
    const payload = { week: wk, target_type: assignType, title };
    if (assignType === "student") {
      if (!assignStudent) return toast.error("Öğrenci seçin");
      payload.student_id = assignStudent;
    } else {
      payload.grade = assignGrade;
      payload.section = assignSection || null;
    }
    try {
      const { data } = await api.post("/study-program/assign-week", payload);
      toast.success(`${data.count} programa hafta eklendi ✓`);
      setAssignFor(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const week = weeks[activeWi];
  const sel = "rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100";

  return (
    <div className="mx-auto max-w-[1400px] space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600"><LayoutGrid className="h-6 w-6" /></div>
          <div>
            <h1 className="font-display text-3xl font-extrabold text-slate-900">Çalışma Programı Düzenle</h1>
            <p className="text-slate-500">Tek öğrenciye veya tüm sınıfa haftalık program &amp; soru hedefleri tanımlayın.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="delete-study-btn" onClick={deleteProgram} className="flex items-center gap-2 rounded-xl bg-rose-50 px-5 py-3 font-bold text-rose-600 transition hover:bg-rose-100 active:scale-95">
            <Trash2 className="h-5 w-5" /> Programı Sil
          </button>
          {targetType === "student" ? (
            <button data-testid="save-study-btn" onClick={saveSingle} disabled={saving} className="flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 font-bold text-white shadow-lg shadow-emerald-200 transition hover:bg-emerald-700 active:scale-95 disabled:opacity-60">
              <Save className="h-5 w-5" /> {saving ? "Kaydediliyor…" : "Öğrenciye Kaydet"}
            </button>
          ) : (
            <button data-testid="study-apply-class-btn" onClick={saveClass} disabled={saving} className="flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 font-bold text-white shadow-lg shadow-indigo-200 transition hover:bg-indigo-700 active:scale-95 disabled:opacity-60">
              <Users className="h-5 w-5" /> {saving ? "Uygulanıyor…" : "Tüm Sınıfa Kaydet"}
            </button>
          )}
        </div>
      </div>

      {/* Target Selector: Öğrenci vs Sınıf */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
          <span className="text-sm font-bold text-slate-700">Hedef Seçimi</span>
          <div className="flex rounded-xl bg-slate-100 p-1">
            <button data-testid="target-student-tab" onClick={() => setTargetType("student")} className={`flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-sm font-bold transition ${targetType === "student" ? "bg-emerald-600 text-white shadow" : "text-slate-500 hover:text-slate-700"}`}>
              <UserRound className="h-4 w-4" /> Tek Öğrenci
            </button>
            <button data-testid="target-class-tab" onClick={() => setTargetType("class")} className={`flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-sm font-bold transition ${targetType === "class" ? "bg-indigo-600 text-white shadow" : "text-slate-500 hover:text-slate-700"}`}>
              <Users className="h-4 w-4" /> Tüm Sınıf
            </button>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 items-end">
          {targetType === "student" ? (
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-semibold text-slate-700">Öğrenci</label>
              <ClassStudentSelect students={students} value={studentId} onChange={(v) => setStudentId(v)} testid="study-student-select" placeholder="Öğrenci seçiniz…" />
            </div>
          ) : (
            <>
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700">Sınıf</label>
                <select data-testid="study-class-grade" value={clsGrade} onChange={(e) => setClsGrade(Number(e.target.value))} className={`${sel} w-full`}>
                  {GRADE_GROUPS.map((grp) => <optgroup key={grp.label} label={grp.label}>{grp.grades.map((g) => <option key={g} value={g}>{g}. Sınıf</option>)}</optgroup>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700">Şube</label>
                <input data-testid="study-class-section" value={clsSection} onChange={(e) => setClsSection(e.target.value)} placeholder="Tüm şubeler" className={`${sel} w-full`} />
              </div>
            </>
          )}
          <div>
            <label className="mb-1 block text-sm font-semibold text-slate-700">Kaynak / Program Adı</label>
            <input data-testid="study-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Örn: HIZ YAYINLARI HİBRİT KİTAPLAR" className={`${sel} w-full`} />
          </div>
        </div>
      </div>

      {/* Editor Body */}
      <div className="space-y-4">
        {targetType === "student" && studentId && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Programlar:</span>
            {programList.map((p) => (
              <button key={p.id} data-testid={`program-chip-${p.id}`} onClick={() => loadProgram(p.id)} className={`rounded-full px-4 py-1.5 text-xs font-bold transition active:scale-95 ${p.id === programId ? "bg-violet-600 text-white shadow" : "border border-slate-200 bg-white text-slate-600 hover:border-violet-300 hover:text-violet-600"}`}>
                {p.title || "Başlıksız"} · {p.week_count} hf
              </button>
            ))}
            <button data-testid="new-program-btn" onClick={newProgram} className="flex items-center gap-1 rounded-full bg-emerald-600 px-4 py-1.5 text-xs font-bold text-white shadow transition hover:bg-emerald-700 active:scale-95">
              <Plus className="h-3.5 w-3.5" /> Yeni Program (Yeni Yayınevi)
            </button>
          </div>
        )}
        {targetType === "class" && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Sınıfın Programları:</span>
            {clsTitles.length === 0 && <span className="text-xs text-slate-400">Bu sınıfta henüz program yok</span>}
            {clsTitles.map((c) => (
              <button key={c.title} data-testid={`class-program-chip-${c.title}`} onClick={() => loadClassTemplate(c.title)} className={`rounded-full px-4 py-1.5 text-xs font-bold transition active:scale-95 ${c.title === clsSelTitle ? "bg-violet-600 text-white shadow" : "border border-slate-200 bg-white text-slate-600 hover:border-violet-300 hover:text-violet-600"}`}>
                {c.title || "Başlıksız"} · {c.week_count} hf · {c.count} öğr.
              </button>
            ))}
            <button data-testid="new-class-program-btn" onClick={newClassProgram} className="flex items-center gap-1 rounded-full bg-emerald-600 px-4 py-1.5 text-xs font-bold text-white shadow transition hover:bg-emerald-700 active:scale-95">
              <Plus className="h-3.5 w-3.5" /> Yeni Program (Yeni Yayınevi)
            </button>
          </div>
        )}
        {/* Hafta sekmeleri */}
        <div className="flex flex-wrap items-center gap-2">
          {weeks.map((w, wi) => (
            <button
              key={wi}
              data-testid={`week-tab-${w.week_number}`}
              onClick={() => setActiveWi(wi)}
              className={`rounded-full px-5 py-2 text-sm font-bold transition active:scale-95 ${wi === activeWi ? "bg-[#1E293B] text-white shadow-lg" : "border border-slate-200 bg-white text-slate-600 hover:border-indigo-300 hover:text-indigo-600"}`}
            >
              {w.month ? `${w.month} · ` : ""}{w.week_number}. Hafta
            </button>
          ))}
          <button data-testid="add-week-btn" onClick={addWeek} className="flex items-center gap-1.5 rounded-full border border-dashed border-slate-300 bg-white px-5 py-2 text-sm font-bold text-slate-500 transition hover:border-indigo-400 hover:text-indigo-600 active:scale-95">
            <Plus className="h-4 w-4" /> Hafta Ekle
          </button>
          <button data-testid="toggle-show-all" onClick={() => setShowAll((v) => !v)} className={`ml-auto rounded-full px-5 py-2 text-sm font-bold transition active:scale-95 ${showAll ? "bg-indigo-600 text-white shadow" : "border border-slate-200 bg-white text-slate-600 hover:border-indigo-300 hover:text-indigo-600"}`}>
            {showAll ? "Sekmeli Görünüm" : "Tüm Haftaları Göster"}
          </button>
        </div>

        {(showAll ? weeks.map((w, i) => [w, i]) : (week ? [[week, activeWi]] : [])).map(([week, activeWi]) => (
          <div key={activeWi} data-testid={`editor-week-${week.week_number}`} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-4">
              <div className="flex flex-wrap items-center gap-3">
                <h3 className="font-display text-lg font-bold text-slate-900">{week.week_number}. Hafta — Ders Satırları × 7 Gün</h3>
                <select data-testid="week-month-select" value={week.month || ""} onChange={(e) => setWeekMonth(activeWi, e.target.value)} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold outline-none focus:border-indigo-400">
                  <option value="">Ay seç…</option>
                  {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                {titleEditWi === activeWi ? (
                  <input
                    autoFocus
                    data-testid={`week-title-input-${activeWi}`}
                    value={week.title || ""}
                    onChange={(e) => setWeekTitle(activeWi, e.target.value)}
                    onBlur={() => setTitleEditWi(null)}
                    onKeyDown={(e) => e.key === "Enter" && setTitleEditWi(null)}
                    placeholder="Hafta başlığı (opsiyonel)"
                    className="w-48 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-indigo-400"
                  />
                ) : (
                  week.title && <span className="rounded-lg bg-indigo-50 px-3 py-1.5 text-sm font-semibold text-indigo-600">{week.title}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => addRow(activeWi)} className="flex items-center gap-1.5 rounded-xl bg-slate-100 px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-indigo-50 hover:text-indigo-600 active:scale-95" data-testid={`add-row-${activeWi}`}>
                  <Plus className="h-4 w-4" /> Ders Satırı Ekle
                </button>
                <button onClick={() => setTitleEditWi(activeWi)} className="flex items-center gap-1 rounded-xl bg-slate-100 px-4 py-2 text-sm font-bold text-slate-600 transition hover:bg-indigo-50 hover:text-indigo-600 active:scale-95" data-testid={`edit-week-${activeWi}`}>
                  <Pencil className="h-4 w-4" /> Hafta Düzenle
                </button>
                {weeks.length > 1 && (
                  <button onClick={() => removeWeek(activeWi)} className="flex items-center gap-1 rounded-xl bg-rose-50 px-4 py-2 text-sm font-bold text-rose-600 transition hover:bg-rose-100 active:scale-95" data-testid={`remove-week-${activeWi}`}>
                    <Trash2 className="h-4 w-4" /> Hafta Sil
                  </button>
                )}
                <button onClick={() => setAssignFor(activeWi)} data-testid={`assign-week-${activeWi}`} className="flex items-center gap-1 rounded-xl bg-indigo-50 px-4 py-2 text-sm font-bold text-indigo-600 transition hover:bg-indigo-100 active:scale-95">
                  <Users className="h-4 w-4" /> Bu Haftayı Ata
                </button>
              </div>
            </div>

            <div className="space-y-4 p-4">
              {week.rows.length === 0 && (
                <div className="rounded-xl border border-dashed border-slate-200 py-8 text-center text-sm text-slate-400">Bu haftada henüz ders satırı yok — "Ders Satırı Ekle" butonuna basarak ders ekleyin.</div>
              )}
              {week.rows.map((row, ri) => (
                <div key={ri} className="rounded-xl border border-slate-200 p-3">
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <select value={row.subject_name} onChange={(e) => pickSubject(activeWi, ri, e.target.value)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold outline-none focus:border-indigo-400" data-testid={`row-subject-${activeWi}-${ri}`}>
                      {subjects.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                    </select>
                    <input value={row.sublabel} onChange={(e) => updateRow(activeWi, ri, { sublabel: e.target.value })} placeholder="Kaynak adı (opsiyonel)" className="min-w-40 flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm outline-none focus:border-indigo-400" data-testid={`row-sublabel-${activeWi}-${ri}`} />
                    <button onClick={() => removeRow(activeWi, ri)} className="ml-auto rounded-lg p-1.5 text-rose-400 transition hover:bg-rose-50 hover:text-rose-600" data-testid={`remove-row-${activeWi}-${ri}`}><X className="h-4 w-4" /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
                    {DAYS.map((day, di) => {
                      const cell = row.cells?.[day] || emptyDay();
                      const hasTask = (cell.unit || "").trim() || (cell.pages || "").trim() || Number(cell.target) > 0;
                      return (
                        <div key={day} className={`rounded-lg border p-2 transition ${hasTask ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-slate-50/50"}`} data-testid={`day-cell-${activeWi}-${ri}-${di}`}>
                          <div className={`mb-1.5 text-center text-[11px] font-extrabold tracking-wider ${hasTask ? "text-emerald-600" : "text-slate-400"}`}>{SHORT_DAYS[di]}</div>
                          <div className="space-y-1">
                            <input value={cell.unit} onChange={(e) => updateCellField(activeWi, ri, day, "unit", e.target.value)} placeholder="1. Ünite" className="w-full rounded border border-slate-200 bg-white px-1.5 py-1 text-xs outline-none focus:border-indigo-400" data-testid={`cell-unit-${activeWi}-${ri}-${day.toLowerCase()}`} />
                            <input value={cell.pages} onChange={(e) => updateCellField(activeWi, ri, day, "pages", e.target.value)} placeholder="8 - 20" className="w-full rounded border border-slate-200 bg-white px-1.5 py-1 text-xs outline-none focus:border-indigo-400" data-testid={`cell-pages-${activeWi}-${ri}-${day.toLowerCase()}`} />
                            <input type="number" min={0} value={cell.target} onChange={(e) => updateCellField(activeWi, ri, day, "target", e.target.value)} placeholder="soru" className="w-full rounded border border-slate-200 bg-white px-1.5 py-1 text-xs outline-none focus:border-indigo-400" data-testid={`cell-target-${activeWi}-${ri}-${day.toLowerCase()}`} />
                            {hasTask && (
                              <div className="grid grid-cols-3 gap-1 border-t border-slate-200 pt-1" data-testid={`cell-dyb-${activeWi}-${ri}-${day.toLowerCase()}`}>
                                <input type="number" min={0} value={cell.dogru || ""} onChange={(e) => updateCellField(activeWi, ri, day, "dogru", e.target.value)} placeholder="D" title="Doğru" className="w-full rounded border border-emerald-200 bg-white px-1 py-1 text-center text-xs font-bold text-emerald-600 outline-none focus:border-emerald-400" data-testid={`cell-dogru-${activeWi}-${ri}-${day.toLowerCase()}`} />
                                <input type="number" min={0} value={cell.yanlis || ""} onChange={(e) => updateCellField(activeWi, ri, day, "yanlis", e.target.value)} placeholder="Y" title="Yanlış" className="w-full rounded border border-rose-200 bg-white px-1 py-1 text-center text-xs font-bold text-rose-600 outline-none focus:border-rose-400" data-testid={`cell-yanlis-${activeWi}-${ri}-${day.toLowerCase()}`} />
                                <input type="number" min={0} value={cell.bos || ""} onChange={(e) => updateCellField(activeWi, ri, day, "bos", e.target.value)} placeholder="B" title="Boş" className="w-full rounded border border-slate-200 bg-white px-1 py-1 text-center text-xs font-bold text-slate-500 outline-none focus:border-slate-400" data-testid={`cell-bos-${activeWi}-${ri}-${day.toLowerCase()}`} />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-slate-100 p-4">
              {targetType === "student" ? (
                <button data-testid="save-study-btn-bottom" onClick={saveSingle} disabled={saving} className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3.5 font-bold text-white transition hover:bg-emerald-700 active:scale-[0.99] disabled:opacity-60">
                  <Save className="h-5 w-5" /> {saving ? "Kaydediliyor…" : "Programı Öğrenciye Kaydet"}
                </button>
              ) : (
                <button data-testid="save-study-class-btn-bottom" onClick={saveClass} disabled={saving} className="flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 py-3.5 font-bold text-white transition hover:bg-indigo-700 active:scale-[0.99] disabled:opacity-60">
                  <Users className="h-5 w-5" /> {saving ? "Uygulanıyor…" : "Tüm Sınıfa Kaydet ve Uygula"}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Haftayı Ata modal */}
      {assignFor !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="assign-week-modal" onClick={() => setAssignFor(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl font-extrabold text-slate-900">{weeks[assignFor]?.week_number}. Haftayı Ata</h3>
            <p className="mb-4 text-sm text-slate-500">Bu haftayı başka bir öğrenciye veya tüm sınıfa ekle (mevcut programın sonuna eklenir).</p>
            <div className="mb-4 flex rounded-xl bg-slate-100 p-1">
              <button data-testid="assign-type-student" onClick={() => setAssignType("student")} className={`flex-1 rounded-lg px-4 py-1.5 text-sm font-bold transition ${assignType === "student" ? "bg-emerald-600 text-white shadow" : "text-slate-500"}`}>
                <UserRound className="mr-1 inline h-4 w-4" /> Öğrenci
              </button>
              <button data-testid="assign-type-class" onClick={() => setAssignType("class")} className={`flex-1 rounded-lg px-4 py-1.5 text-sm font-bold transition ${assignType === "class" ? "bg-indigo-600 text-white shadow" : "text-slate-500"}`}>
                <Users className="mr-1 inline h-4 w-4" /> Tüm Sınıf
              </button>
            </div>
            {assignType === "student" ? (
              <ClassStudentSelect students={students} value={assignStudent} onChange={setAssignStudent} testid="assign-student-select" placeholder="Öğrenci seçiniz…" />
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <select data-testid="assign-grade" value={assignGrade} onChange={(e) => setAssignGrade(Number(e.target.value))} className={`${sel} w-full`}>
                  {GRADE_GROUPS.map((grp) => <optgroup key={grp.label} label={grp.label}>{grp.grades.map((g) => <option key={g} value={g}>{g}. Sınıf</option>)}</optgroup>)}
                </select>
                <input data-testid="assign-section" value={assignSection} onChange={(e) => setAssignSection(e.target.value)} placeholder="Tüm şubeler" className={`${sel} w-full`} />
              </div>
            )}
            <div className="mt-5 flex gap-2">
              <button data-testid="assign-week-cancel" onClick={() => setAssignFor(null)} className="flex-1 rounded-xl border border-slate-200 py-2.5 font-bold text-slate-500 transition hover:bg-slate-50">İptal</button>
              <button data-testid="assign-week-confirm" onClick={doAssignWeek} className="flex-1 rounded-xl bg-indigo-600 py-2.5 font-bold text-white transition hover:bg-indigo-700 active:scale-95">Haftayı Ata</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
